import { frameShell, type FrameShell } from '../shell/shellOrigin'

/**
 * A YOUTUBE LINK, PLAYED INSIDE THE APP — owner, 2026-10-19: 只要使用者或者是任何 agent 把一個
 * YouTube 的連結貼在 session 裡面, 那 Clean View 就可以播放該連結的 YouTube video — and then:
 * 這個有辦法直接使用 in-app browser 來做到嗎?
 *
 * It opens in the app's cover panel (the one a picture in Clean View opens in), and what is
 * framed there is YouTube's EMBED PLAYER, not youtube.com: YouTube refuses to let its own
 * pages be framed by anyone else's app, and offers the player for exactly this. So the
 * in-app view plays, pauses, goes full screen and shows captions; comments and the
 * recommendations stay on YouTube, one tap away.
 *
 * youtube-nocookie.com, YouTube's own privacy-enhanced host for the same player: nothing is
 * stored in the browser until the person presses play.
 */
export type YouTubeVideo = {
  id: string
  /** Seconds into the video, from `t=` / `start=`. 0 = the beginning. */
  start: number
  /** A Short: drawn upright. */
  short: boolean
  /** The link as written — what "Open in YouTube" opens. */
  url: string
}

const ID = /^[A-Za-z0-9_-]{11}$/
const HOSTS = new Set(['youtube.com', 'www.youtube.com', 'm.youtube.com', 'music.youtube.com'])
const NOCOOKIE = new Set(['youtube-nocookie.com', 'www.youtube-nocookie.com'])
/** Path forms that carry the id as their second segment. */
const PATH_FORMS = new Set(['shorts', 'live', 'embed', 'v'])

/** `90`, `90s`, `1m30s`, `1h2m3s` → seconds; anything else → 0. */
function seconds(raw: string | null): number {
  if (!raw) return 0
  if (/^\d+$/.test(raw)) return Number(raw)
  const m = /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/.exec(raw)
  if (!m) return 0
  return Number(m[1] ?? 0) * 3600 + Number(m[2] ?? 0) * 60 + Number(m[3] ?? 0)
}

/** One link → the video it names, or null when it names none (a channel, a search, another site). */
export function youtubeVideo(href: string): YouTubeVideo | null {
  let u: URL
  try {
    u = new URL(href)
  } catch {
    return null
  }
  if (u.protocol !== 'https:' && u.protocol !== 'http:') return null
  const host = u.hostname.toLowerCase()
  const parts = u.pathname.split('/').filter(Boolean)
  let id: string | undefined
  let short = false
  if (host === 'youtu.be') {
    id = parts[0]
  } else if (HOSTS.has(host) || NOCOOKIE.has(host)) {
    if (parts[0] === 'watch') id = u.searchParams.get('v') ?? undefined
    else if (parts[0] && PATH_FORMS.has(parts[0])) {
      id = parts[1]
      short = parts[0] === 'shorts'
    }
  }
  if (!id || !ID.test(id)) return null
  const hashT = /(?:^|&)t=([^&]+)/.exec(u.hash.slice(1))?.[1] ?? null
  const start = seconds(u.searchParams.get('t') ?? u.searchParams.get('start') ?? hashT)
  return { id, start, short, url: href }
}

/** Code is quoted, not offered: a link inside ``` or `…` is something to read, not to play. */
function withoutCode(text: string): string {
  return text.replace(/```[\s\S]*?(?:```|$)/g, ' ').replace(/`[^`\n]*`/g, ' ')
}

/** At most this many players under one message; the rest stay ordinary links in the text. */
const YOUTUBE_PER_MESSAGE = 3

/**
 * Every video a message links to, in the order written, each once. A bare link, a
 * Markdown link `[title](url)` and an `<url>` all count; one inside code does not.
 */
export function youtubeLinks(text: string): YouTubeVideo[] {
  const out: YouTubeVideo[] = []
  const seen = new Set<string>()
  for (const m of withoutCode(text).matchAll(/https?:\/\/[^\s<>()[\]"'`]+/g)) {
    // A sentence's own punctuation is not part of the link it ends with.
    const v = youtubeVideo(m[0].replace(/[.,;:!?]+$/, ''))
    if (!v || seen.has(v.id)) continue
    seen.add(v.id)
    out.push(v)
    if (out.length === YOUTUBE_PER_MESSAGE) break
  }
  return out
}

export function youtubeThumb(id: string): string {
  return `https://i.ytimg.com/vi/${id}/hqdefault.jpg`
}

export function youtubeEmbed(v: YouTubeVideo): string {
  const q = new URLSearchParams({ autoplay: '1', playsinline: '1', rel: '0' })
  if (v.start > 0) q.set('start', String(v.start))
  return `https://www.youtube-nocookie.com/embed/${v.id}?${q.toString()}`
}

/**
 * WHETHER THIS APP MAY FRAME THE PLAYER AT ALL — the same rule as a friend's artifact
 * (artifacts/everywhere.ts `sharedViewGate`): a frame that is not ours can post to a
 * Capacitor shell's native bridge, so inside the iPhone / Android app it is framed only
 * by a build that says it guards that bridge (`AvengentsFrameGuard/<n>`). Anywhere else
 * the link opens outside, in YouTube itself, as it always did.
 */
export function canPlayInApp(shell: FrameShell = frameShell()): boolean {
  return !(shell.kind === 'capacitor' && !shell.frameGuard)
}

/**
 * The player's frame: YouTube's page is someone else's, so inside a shell it gets the
 * bare set — no popups, no downloads, no dialogs, each a road into the shell rather than
 * a convenience there (everywhere.ts `contentFrameAttrs`). A plain browser keeps popups,
 * so YouTube's own logo still opens the video on YouTube in a tab.
 */
export function youtubeSandbox(shell: FrameShell = frameShell()): string {
  const bare = 'allow-scripts allow-same-origin allow-presentation'
  return shell.kind === 'none' ? `${bare} allow-popups allow-popups-to-escape-sandbox` : bare
}
